import { UnitAI } from './UnitAI.js';
import { BASE_CONFIG, RESOURCE_CONFIG, GAME_CONFIG, DEBUG_VISUALIZATION_CONFIG } from '../config.js';
import { getDistance } from '../utils/math.js';
import { isDebugMode, debugLog } from '../utils/debug.js';
import { Projectile } from '../entities/Projectile.js';
import { audioManager } from '../systems/AudioManager.js';
import { createTeamBaseStateMachine, guards, events, createContext } from './TeamBaseStateMachine.js';
import { interpret } from 'robot3';

// AI for team base entities - handles defense, resource management, and spawning
export class TeamBaseAI extends UnitAI {
  constructor(unit) {
    super(unit);
    
    // Override perception for bases
    this.perceptionRange = BASE_CONFIG.PERCEPTION_RANGE;
    
    // Combat properties
    this.fireRate = BASE_CONFIG.FIRE_RATE;
    this.lastFireTime = 0;
    
    // Target tracking for predictive aiming
    this.targetPrevX = null;
    this.targetPrevY = null;
    this.targetPrevTime = 0;
    this.targetInitialized = false;
    
    // Resource management
    this.targetResource = null; // Specific resource to direct units to
    this.lastResourceCheck = 0;
    this.resourceCheckInterval = 2000; // Check every 2 seconds
    
    // Initialize state machine
    this.stateMachine = createTeamBaseStateMachine();
    const initialContext = createContext(unit, this);
    this.stateService = interpret(this.stateMachine, () => {
      this.onStateMachineChange();
    }, initialContext);

    // Send initialization event immediately to transition from idle to operating
    // This ensures bases can defend right away instead of waiting 100ms
    debugLog('TeamBase', {
      action: 'sending_initialized',
      team: unit.team.name,
      currentState: this.stateService.machine.current
    });
    this.stateService.send(events.INITIALIZED);
  }
  
  // Initialize base-specific states
  initializeStates() {
    super.initializeStates();

    // Base states
    this.stateHandlers.set('idle', this.handleIdleState.bind(this));
    this.stateHandlers.set('operating', this.handleOperatingState.bind(this));
    this.stateHandlers.set('defending', this.handleDefendingState.bind(this));
    this.stateHandlers.set('requesting_metal', this.handleRequestingMetalState.bind(this));
    this.stateHandlers.set('requesting_ammo', this.handleRequestingAmmoState.bind(this));
    this.stateHandlers.set('requesting_organics', this.handleRequestingOrganicsState.bind(this));
    this.stateHandlers.set('repairing', this.handleRepairingState.bind(this));
    this.stateHandlers.set('destroyed', this.handleDestroyedState.bind(this));
  }

  // Handle idle state - bases should still defend even while idle
  // This provides a fallback defense mechanism before state machine transitions
  handleIdleState() {
    const base = this.unit;

    // Still try to defend even in idle state (shouldn't normally be here after initialization)
    if (this.currentTarget && this.canFireAt(this.currentTarget)) {
      this.fireAt(this.currentTarget);
    } else if (this.nearbyEnemies.length > 0) {
      this.findBestTarget();
      if (this.currentTarget && this.canFireAt(this.currentTarget)) {
        this.fireAt(this.currentTarget);
      }
    }
  }
  
  // Handle state machine changes
  onStateMachineChange() {
    const newStateName = this.stateService.machine.current;
    if (newStateName !== this.unit.state) {
      this.unit.setState(newStateName);
      debugLog('TeamBase', {
        team: this.unit.team.name,
        state: newStateName,
        ammo: this.unit.ammo,
        health: this.unit.health
      });
    }
  }
  
  // Override perception update for bases
  updatePerception(soldiers, projectiles, resources, bases) {
    super.updatePerception(soldiers, projectiles, resources, bases);
    
    // Update perception range based on kills
    const base = this.unit;
    if (base.missileKills > 0) {
      this.perceptionRange = BASE_CONFIG.PERCEPTION_RANGE + 
        (base.missileKills * BASE_CONFIG.PERCEPTION_KILL_BONUS);
    }
  }
  
  // Main decision making
  makeDecision() {
    const base = this.unit;
    const now = Date.now();
    
    // Don't make decisions if destroyed
    if (!base.alive) {
      this.stateService.send(events.DESTROYED);
      return;
    }
    
    // Update context for state machine
    const context = createContext(base, this);
    this.stateService.context = context;
    
    // Update target tracking for automated defense
    // But don't send state change events - let the base fire while maintaining its priority
    if (this.nearbyEnemies.length > 0) {
      this.findBestTarget();
    } else if (this.currentTarget) {
      this.currentTarget = null;
    }
    
    // Check health status
    if (base.health < base.maxHealth * 0.5) {
      this.stateService.send(events.HEALTH_CRITICAL);
    } else if (base.health >= base.maxHealth && base.state === 'repairing') {
      this.stateService.send(events.REPAIR_COMPLETE);
    }
    
    // Check resource needs
    if (base.ammo < base.maxAmmo * 0.1) {
      this.stateService.send(events.CRITICAL_AMMO);
    } else if (base.ammo < base.maxAmmo * 0.3) {
      this.stateService.send(events.LOW_AMMO);
    } else if (base.resources.metal < base.maxMetal * 0.5) {
      this.stateService.send(events.LOW_METAL);
    } else if (base.resources.organics < 20) { // Less than 2 units worth
      this.stateService.send(events.LOW_ORGANICS);
    }
    
    // Check for repair
    const repairingSoldier = this.findRepairingSoldier();
    if (repairingSoldier && base.state !== 'repairing') {
      this.stateService.send(events.REPAIR_STARTED);
    }
    
    // Periodic condition check
    if (now - this.lastDecision > 1000) {
      this.stateService.send(events.CHECK_CONDITIONS);
      this.lastDecision = now;
    }
    
    // Update priority for soldier reference
    this.updatePriority();
  }
  
  // State handlers
  handleOperatingState() {
    const base = this.unit;

    // Normal operations - convert metal to ammo if needed
    if (base.resources.metal > 0 && base.ammo < base.maxAmmo) {
      const ammoNeeded = base.maxAmmo - base.ammo;
      const metalNeeded = Math.ceil(ammoNeeded / RESOURCE_CONFIG.METAL_TO_AMMO);
      const metalToUse = Math.min(base.resources.metal, metalNeeded, 10); // Convert up to 10 per frame

      if (metalToUse > 0) {
        base.resources.metal -= metalToUse;
        base.ammo += metalToUse * RESOURCE_CONFIG.METAL_TO_AMMO;
        base.ammo = Math.min(base.ammo, base.maxAmmo);
      }
    }

    // Passive healing while operating (very slow - 0.5 health per second)
    if (base.health < base.maxHealth) {
      const healPerFrame = base.maxHealth / (60 * 2000); // 0.5 health per second
      base.health = Math.min(base.maxHealth, base.health + healPerFrame);
    }

    // Fire missiles at enemies in range (automated defense)
    if (this.currentTarget && this.canFireAt(this.currentTarget)) {
      this.fireAt(this.currentTarget);
    } else if (this.nearbyEnemies.length > 0) {
      // Find new target if we lost the current one
      this.findBestTarget();
      if (this.currentTarget && this.canFireAt(this.currentTarget)) {
        this.fireAt(this.currentTarget);
      }
    }
    
    // Clear target resource if not needed
    if (!guards.needsAmmo(createContext(base, this)) && !guards.needsOrganics(createContext(base, this))) {
      this.targetResource = null;
    }
  }
  
  handleDefendingState() {
    const base = this.unit;
    
    // Fire at current target if possible
    if (this.currentTarget && this.canFireAt(this.currentTarget)) {
      this.fireAt(this.currentTarget);
    } else {
      // Find new target
      this.findBestTarget();
    }
    
    // Passive healing while defending (slower than repair by max-level soldier)
    // This prevents bases from being stuck in defend mode forever
    if (base.health < base.maxHealth) {
      const healPerFrame = base.maxHealth / (60 * 1000); // 1 health per second (60fps * 1000ms)
      base.health = Math.min(base.maxHealth, base.health + healPerFrame);
    }
  }
  
  handleRequestingMetalState() {
    const base = this.unit;
    const now = Date.now();
    
    // Periodically check for nearby metal resources
    if (now - this.lastResourceCheck > this.resourceCheckInterval) {
      this.lastResourceCheck = now;
      this.findNearbyMetalResource();
    }
    
    // Still defend if enemies appear
    if (this.currentTarget && this.canFireAt(this.currentTarget)) {
      this.fireAt(this.currentTarget);
    }
  }
  
  handleRequestingAmmoState() {
    const base = this.unit;
    
    // Critical state - accept any resources and ammo donations
    // Soldiers will check this state and donate if possible
    
    // IMPORTANT: Convert metal to ammo if available!
    if (base.resources.metal > 0 && base.ammo < base.maxAmmo) {
      const ammoNeeded = base.maxAmmo - base.ammo;
      const metalNeeded = Math.ceil(ammoNeeded / RESOURCE_CONFIG.METAL_TO_AMMO);
      const metalToUse = Math.min(base.resources.metal, metalNeeded, 10); // Convert up to 10 per frame
      
      if (metalToUse > 0) {
        base.resources.metal -= metalToUse;
        base.ammo += metalToUse * RESOURCE_CONFIG.METAL_TO_AMMO;
        base.ammo = Math.min(base.ammo, base.maxAmmo);
        
        debugLog('Base', {
          action: 'emergency_metal_conversion',
          team: base.team.name,
          metalUsed: metalToUse,
          ammoGained: metalToUse * RESOURCE_CONFIG.METAL_TO_AMMO,
          totalAmmo: base.ammo,
          metalRemaining: base.resources.metal
        });
        
        // Check if we have enough ammo now
        if (base.ammo >= base.maxAmmo * 0.3) {
          this.stateService.send(events.RESOURCE_RECEIVED);
        }
      }
    }
    
    // Still try to defend with remaining ammo
    if (this.currentTarget && base.ammo >= BASE_CONFIG.MISSILE_AMMO_COST) {
      this.fireAt(this.currentTarget);
    }
  }
  
  handleRepairingState() {
    const base = this.unit;
    
    // Being repaired - check if repair soldier is still present
    const repairingSoldier = this.findRepairingSoldier();
    if (!repairingSoldier) {
      // Lost repair soldier
      this.stateService.send(events.CHECK_CONDITIONS);
    }
    
    // Can still defend while being repaired
    if (this.currentTarget && this.canFireAt(this.currentTarget)) {
      this.fireAt(this.currentTarget);
    }
  }
  
  handleRequestingOrganicsState() {
    const base = this.unit;
    const now = Date.now();
    
    // Periodically check for nearby nebulas with open slots
    if (now - this.lastResourceCheck > this.resourceCheckInterval) {
      this.lastResourceCheck = now;
      this.findNearbyNebulaWithSlots();
    }
    
    // Still defend if enemies appear
    if (this.currentTarget && this.canFireAt(this.currentTarget)) {
      this.fireAt(this.currentTarget);
    }
  }
  
  handleDestroyedState() {
    const base = this.unit;
    
    // Set base as dead when entering destroyed state
    base.alive = false;
    
    // Base is destroyed - wait for revival
    // Clear all targets and resources
    this.currentTarget = null;
    this.targetResource = null;
    
    // Clear any ongoing actions
    base.vx = 0;
    base.vy = 0;
  }
  
  // Combat methods
  findBestTarget() {
    const base = this.unit;
    let bestTarget = null;
    let bestScore = -Infinity;
    
    for (const enemy of this.nearbyEnemies) {
      const distance = getDistance(base, enemy);
      
      // Skip if out of weapon range
      if (distance > base.weaponRange) continue;
      
      // Score based on threat level
      // Prioritize: closer enemies, lower health enemies, higher level enemies
      const distanceScore = (base.weaponRange - distance) / base.weaponRange;
      const healthScore = 1 - (enemy.health / enemy.maxHealth);
      const levelScore = (enemy.level || 1) / GAME_CONFIG.MAX_LEVEL;
      
      const score = distanceScore * 2 + healthScore + levelScore;
      
      if (score > bestScore) {
        bestScore = score;
        bestTarget = enemy;
      }
    }
    
    // Reset tracking if target changed
    if (this.currentTarget !== bestTarget) {
      this.targetInitialized = false;
      this.targetPrevX = null;
      this.targetPrevY = null;
    }
    
    this.currentTarget = bestTarget;
  }
  
  canFireAt(target) {
    const base = this.unit;
    const now = Date.now();
    
    // Check basic requirements
    if (!target || !target.alive || target.health <= 0) return false;
    if (!this.isValidTarget(target)) return false;
    if (base.ammo < BASE_CONFIG.MISSILE_AMMO_COST) return false;
    if (now - this.lastFireTime < this.fireRate) return false;
    
    // Check range
    const distance = getDistance(base, target);
    return distance <= base.weaponRange;
  }
  
  fireAt(target) {
    const base = this.unit;
    const now = Date.now();
    
    // Don't fire if base is destroyed
    if (!base.alive) return;
    
    if (!this.canFireAt(target)) return;
    
    // Initialize tracking if needed
    if (!this.targetInitialized) {
      this.targetPrevX = target.x;
      this.targetPrevY = target.y;
      this.targetPrevTime = now;
      this.targetInitialized = true;
      // Need to wait one frame to get velocity
      return;
    }
    
    // Calculate target velocity for prediction
    const deltaTime = now - this.targetPrevTime;
    const frameTime = 1000 / 60; // 60 FPS
    const framesElapsed = Math.max(1, deltaTime / frameTime);
    
    let targetVx = (target.x - this.targetPrevX) / framesElapsed;
    let targetVy = (target.y - this.targetPrevY) / framesElapsed;
    
    // Cap velocity to prevent wild predictions
    const maxVel = 5;
    targetVx = Math.max(-maxVel, Math.min(maxVel, targetVx));
    targetVy = Math.max(-maxVel, Math.min(maxVel, targetVy));
    
    // Calculate prediction
    const dx = target.x - base.x;
    const dy = target.y - base.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const projectileSpeed = 2 + (base.level - 1) * 0.5;
    const timeToTarget = distance / projectileSpeed;
    
    // Bases are skilled predictors
    const predictionAccuracy = 0.8;
    const predictedX = target.x + (targetVx * timeToTarget * predictionAccuracy);
    const predictedY = target.y + (targetVy * timeToTarget * predictionAccuracy);
    
    // Add small spread for realism
    const spread = 5;
    const randomX = (Math.random() - 0.5) * spread;
    const randomY = (Math.random() - 0.5) * spread;
    
    // Create missile projectile (perfect homing is handled by Projectile class)
    const projectile = new Projectile(
      base.x, base.y,
      predictedX + randomX, predictedY + randomY,
      base.damage,
      base.color,
      base,
      base.canvas,
      target // Pass target for homing
    );
    
    // Add to projectiles array
    if (base.projectiles) {
      base.projectiles.push(projectile);
    }
    
    // Consume ammo and update fire time
    base.ammo -= BASE_CONFIG.MISSILE_AMMO_COST;
    this.lastFireTime = now;
    
    // Play sound
    audioManager.playFire();
    
    // Update tracking for next shot
    this.targetPrevX = target.x;
    this.targetPrevY = target.y;
    this.targetPrevTime = now;
    
    // Send fired event
    this.stateService.send(events.MISSILE_FIRED);
    
    debugLog('TeamBase', {
      action: 'fired_missile',
      team: base.team.name,
      targetId: target.id,
      ammoLeft: base.ammo,
      missilesLeft: Math.floor(base.ammo / BASE_CONFIG.MISSILE_AMMO_COST)
    });
  }
  
  // Resource management
  findNearbyMetalResource() {
    const base = this.unit;
    let nearestMetal = null;
    let nearestDistance = Infinity;
    
    for (const resource of this.nearbyResources) {
      if (resource.type !== 'metal' || !resource.active) continue;
      
      const distance = getDistance(base, resource);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestMetal = resource;
      }
    }
    
    // Update target resource for soldiers to see
    this.targetResource = nearestMetal;
    base.targetResource = nearestMetal;
    
    if (nearestMetal) {
      debugLog('TeamBase', {
        action: 'found_metal',
        team: base.team.name,
        distance: nearestDistance.toFixed(0)
      });
    }
  }
  
  // Priority management for soldier AI integration
  updatePriority() {
    const base = this.unit;
    const state = base.state;
    
    // Map states to priorities
    switch (state) {
      case 'defending':
      case 'repairing':
        base.priority = 'defend';
        break;
        
      case 'requesting_metal':
        if (this.targetResource) {
          base.priority = 'get_nearby_metal';
        } else {
          base.priority = 'get_metal';
        }
        break;
        
      case 'requesting_ammo':
        base.priority = 'get_ammo';
        break;
        
      case 'requesting_organics':
        if (this.targetResource) {
          base.priority = 'get_organics';
        } else {
          base.priority = 'hunt';
        }
        break;
        
      case 'destroyed':
        base.priority = 'defend'; // Soldiers should defend the area
        break;
        
      default:
        base.priority = 'hunt';
    }
  }
  
  // Find nearby nebula with open slots
  findNearbyNebulaWithSlots() {
    const base = this.unit;
    let nearestNebula = null;
    let nearestDistance = Infinity;
    
    for (const resource of this.nearbyResources) {
      if (resource.type !== 'nebula' || !resource.active) continue;
      if (resource.gatherers.size >= RESOURCE_CONFIG.NEBULA_MAX_GATHERERS) continue;
      
      const distance = getDistance(base, resource);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestNebula = resource;
      }
    }
    
    // Update target resource for soldiers to see
    this.targetResource = nearestNebula;
    base.targetResource = nearestNebula;
    
    if (nearestNebula) {
      debugLog('TeamBase', {
        action: 'found_nebula',
        team: base.team.name,
        distance: nearestDistance.toFixed(0),
        openSlots: RESOURCE_CONFIG.NEBULA_MAX_GATHERERS - nearestNebula.gatherers.size
      });
    }
  }
  
  // Find max-level soldier attempting repair
  findRepairingSoldier() {
    if (!this.nearbyAllies) return null;
    
    for (const ally of this.nearbyAllies) {
      if (ally.level >= GAME_CONFIG.MAX_LEVEL && 
          ally.state === 'repairing_base' &&
          getDistance(this.unit, ally) < BASE_CONFIG.CENTER_THRESHOLD) {
        return ally;
      }
    }
    
    return null;
  }
  
  // Handle resource deposits
  onResourceDeposited(type, amount) {
    const base = this.unit;
    
    // Send event based on what was received
    if (type === 'metal' || type === 'organics' || type === 'crystal') {
      this.stateService.send(events.RESOURCE_RECEIVED);
    }
    
    debugLog('TeamBase', {
      action: 'resource_received',
      team: base.team.name,
      type: type,
      amount: amount
    });
  }
  
  // Handle ammo donations from soldiers
  onAmmoDonated(amount) {
    this.stateService.send(events.AMMO_DONATED);
    
    debugLog('TeamBase', {
      action: 'ammo_donated',
      team: this.unit.team.name,
      amount: amount,
      total: this.unit.ammo
    });
  }
  
  // Handle damage
  onDamageTaken(damage, attacker) {
    this.stateService.send(events.DAMAGE_TAKEN);
    
    // If we have no current target, prioritize the attacker
    if (!this.currentTarget && attacker && this.isValidTarget(attacker)) {
      this.currentTarget = attacker;
    }
  }
  
  // Handle repair completion
  onRepairComplete() {
    if (this.unit.state === 'repairing') {
      this.stateService.send(events.REPAIR_COMPLETE);
    }
    
    debugLog('TeamBase', {
      action: 'repair_complete',
      team: this.unit.team.name,
      health: this.unit.health,
      activeRepairers: this.unit.activeRepairers.size
    });
  }
  
  // Debug rendering
  renderDebug(ctx) {
    if (!isDebugMode()) return;
    
    const base = this.unit;
    
    // Draw perception range - commented out as it's now handled in TeamBase.render()
    // ctx.strokeStyle = base.color;
    // ctx.globalAlpha = 0.1;
    // ctx.lineWidth = 2;
    // ctx.beginPath();
    // ctx.arc(base.x, base.y, this.perceptionRange, 0, Math.PI * 2);
    // ctx.stroke();
    
    // Draw weapon range - commented out as it's now handled in TeamBase.render()
    // ctx.strokeStyle = base.color;
    // ctx.globalAlpha = 0.2;
    // ctx.lineWidth = 1;
    // ctx.beginPath();
    // ctx.arc(base.x, base.y, base.weaponRange, 0, Math.PI * 2);
    // ctx.stroke();
    
    ctx.globalAlpha = 1.0;
    
    // Draw line to current target
    if (DEBUG_VISUALIZATION_CONFIG.showTargetLines && this.currentTarget) {
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(base.x, base.y);
      ctx.lineTo(this.currentTarget.x, this.currentTarget.y);
      ctx.stroke();
    }
    
    // Draw line to target resource
    if (DEBUG_VISUALIZATION_CONFIG.showTargetLines && this.targetResource) {
      ctx.strokeStyle = '#ffff00';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(base.x, base.y);
      ctx.lineTo(this.targetResource.x, this.targetResource.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }
}